import React from 'react';
import { motion } from 'framer-motion';

const SCENARIOS = [
  {
    timing: "Night before a lender meeting",
    headline: "You're building a DSCR report from scratch — again.",
    detail: "You've done this a dozen times. Pull rent rolls from one spreadsheet, mortgage payments from another, NOI calculations from a third. It takes 3–4 hours. You still aren't confident the numbers are right.",
    cost: "3–4 hours every deal",
    fix: "VaWeNa generates your lender-ready DSCR report in one click.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    timing: "You've found a property you want",
    headline: "You don't know if you can actually afford it.",
    detail: "You think your DTI is around 38%. You think you have enough liquidity. But \"think\" isn't good enough when you're about to make an offer on a $600K property. You need to know.",
    cost: "Missed deals, wrong timing",
    fix: "WealthScope's Deal Readiness Score tells you exactly where you stand before you make a move.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    timing: "Tax season, every year",
    headline: "Your CPA needs rental data from 4 properties. You start digging.",
    detail: "Rental income: which spreadsheet? Repairs: check the email chain. Mortgage interest: log into 3 lender portals. You spend a full weekend on data your CPA charges you $400 to process.",
    cost: "$400–$800 in CPA time + a lost weekend",
    fix: "PortfolioIQ generates your tax summary automatically — one clean export, ready for your CPA.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
];

export const ProblemSection: React.FC = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 w-[500px] h-[500px] dark:bg-blue-600/10 bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[1100px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5">
            Sound familiar?
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-4 leading-tight">
            Real estate investing is hard enough.
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> The admin work shouldn't be.</span>
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-xl mx-auto leading-relaxed">
            Most investors manage their portfolio across 5+ spreadsheets, 3 lender portals, and a folder of receipts. Here's what that looks like in practice.
          </p>
        </div>

        {/* Scenario cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {SCENARIOS.map((s, i) => (
            <motion.div
              key={s.headline}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="relative flex h-full min-h-[420px] flex-col rounded-2xl border border-blue-300/80 p-6 dark:border-blue-500/20 dark:bg-white/[0.03] bg-white transition-all duration-300 ease-out hover:-translate-y-1 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 dark:hover:border-blue-500/40 dark:hover:shadow-blue-500/10 group"
            >
              {/* Timing badge */}
              <div className="mb-4 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest dark:text-slate-500 text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full dark:bg-blue-400 bg-blue-500 transition-transform duration-300 group-hover:scale-125" />
                {s.timing}
              </div>

              {/* Headline */}
              <h3 className="mb-3 min-h-[64px] text-base font-bold leading-snug dark:text-white text-gray-900">
                <span className="inline-flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg dark:bg-blue-500/10 bg-blue-100 dark:text-blue-400 text-blue-600 transition-transform duration-300 group-hover:scale-105">
                    {s.icon}
                  </span>
                  <span>{s.headline}</span>
                </span>
              </h3>

              {/* Detail */}
              <p className="mb-5 min-h-[124px] flex-1 text-sm leading-relaxed dark:text-slate-400 text-gray-500">
                {s.detail}
              </p>

              {/* Cost */}
              <div className="mb-3 flex items-center gap-2 rounded-xl border p-3 dark:border-white/[0.06] border-gray-100 dark:bg-white/[0.03] bg-slate-50 transition-colors duration-300 group-hover:dark:bg-white/[0.05] group-hover:bg-blue-50/60">
                <svg className="w-4 h-4 shrink-0 dark:text-slate-400 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-semibold dark:text-slate-400 text-gray-500">Cost: {s.cost}</span>
              </div>

              {/* Fix */}
              <div className="flex items-start gap-2 transition-transform duration-300 group-hover:translate-x-0.5">
                <svg className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-semibold dark:text-slate-300 text-gray-600 leading-snug">{s.fix}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bridge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-sm dark:text-slate-400 text-gray-500">
            Every one of these problems has a specific fix inside VaWeNa.{' '}
            <span className="dark:text-white text-gray-900 font-semibold">Here's how it works.</span>
          </p>
          <div className="mt-4 flex justify-center">
            <svg className="w-5 h-5 dark:text-slate-500 text-gray-400 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
